import { LitElement, html, css, nothing, type PropertyValues } from "lit";
import { customElement, property, state } from "lit/decorators.js";
import { api } from "../api";
import { formatProductName } from "../product-utils";
import type { FeatureFlag, FlagEnv, ProductsResponse } from "../types";

import "../components/create-flag-dialog";

const ENVS: FlagEnv[] = ["dev", "stage", "prod"];

@customElement("flags-page")
export class FlagsPage extends LitElement {
  static styles = css`
    :host { display: flex; flex: 1; flex-direction: column; min-height: 0; font-family: Geist, sans-serif; }
    .controls { display: flex; align-items: center; gap: var(--spacing-12, 12px); margin-bottom: var(--spacing-16, 16px); }
    .spacer { margin-left: auto; }
    .search-input { display: block; width: 220px; margin-bottom: var(--spacing-12, 12px); }
    bee-card { --padding: 0; }
    .card-content { padding: var(--spacing-16, 16px); overflow-x: auto; }
    .flags-table { width: 100%; border-collapse: collapse; }
    .flags-table th {
      text-align: center;
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      font-weight: 600; font-size: 11px; text-transform: uppercase; letter-spacing: 0.5px;
      color: var(--colors-foreground-fg-gray-700, #5a6072);
      border-bottom: 2px solid var(--colors-border-border-gray-300, #e0e3e8);
    }
    .flags-table th.name-col { text-align: left; }
    .flags-table td {
      padding: var(--spacing-8, 8px) var(--spacing-12, 12px);
      border-bottom: 1px solid var(--colors-border-border-gray-300, #e0e3e8);
      font-size: 13px; vertical-align: middle;
    }
    .flag-key { font-family: monospace; font-weight: 600; }
    .flag-desc { font-size: 11px; color: var(--colors-foreground-fg-gray-500, #98a2b3); }
    .cell { display: flex; align-items: center; justify-content: center; gap: var(--spacing-8, 8px); }
    .rollout { width: 64px; }
    .env-col { text-align: center; }
    .empty { padding: var(--spacing-24, 24px); text-align: center; font-size: 13px; color: var(--colors-foreground-fg-gray-700, #5a6072); }
    .enable-panel {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: var(--spacing-12, 12px);
      padding: var(--spacing-24, 24px);
      text-align: center;
    }
    .enable-msg { font-size: 14px; color: var(--colors-foreground-fg-gray-700, #5a6072); }
    .audit-meta { color: var(--colors-foreground-fg-gray-500, #98a2b3); }
  `;

  @property() activeProductFilter: string | null = null;
  @property({ attribute: false }) products: ProductsResponse | null = null;

  @state() private _flags: FeatureFlag[] = [];
  @state() private _search = "";
  @state() private _loading = true;
  @state() private _showCreate = false;
  @state() private _enabling = false;
  @state() private _enablePrUrl: string | null = null;

  // Master switch: is Flagship enabled for this product (manifest featureFlags)?
  private get _flagsEnabled(): boolean {
    const key = this.activeProductFilter;
    return !!(key && this.products?.products?.[key]?.featureFlags);
  }

  // Single load trigger: guarantees exactly one _load() on first render (regardless
  // of whether activeProductFilter/products differ from their defaults) and one more
  // per subsequent change — avoiding the duplicate-fetch that connectedCallback +
  // updated() both calling _load() would cause when those properties are already set.
  private _hasLoadedOnce = false;

  updated(changed: PropertyValues<this>) {
    const isFirstPass = !this._hasLoadedOnce;
    this._hasLoadedOnce = true;
    if (isFirstPass || changed.has("activeProductFilter") || changed.has("products")) {
      if (!isFirstPass && changed.has("activeProductFilter")) {
        this._enablePrUrl = null;
        this._search = "";
      }
      this._load();
    }
  }

  private _toast(message: string, variant: "error" | "success" | "warning" = "error") {
    const alert = Object.assign(document.createElement("bee-alert"), { variant, closable: true, duration: 5000 }) as HTMLElement & { toast(): Promise<void> };
    const span = document.createElement("span");
    span.slot = "message";
    span.textContent = message;
    alert.append(span);
    document.body.append(alert);
    alert.toast();
  }

  private async _enable() {
    if (!this.activeProductFilter || this._enabling) return;
    this._enabling = true;
    try {
      const res = await api.enableFlags(this.activeProductFilter);
      this._enablePrUrl = res.prUrl;
    } catch (e: unknown) {
      this._toast(e instanceof Error ? e.message : "Failed to open enable PR");
    } finally {
      this._enabling = false;
    }
  }

  private async _load() {
    // Skip the Flagship fetch entirely for products that haven't opted in.
    if (!this.activeProductFilter || !this._flagsEnabled) {
      this._flags = [];
      this._loading = false;
      return;
    }
    this._loading = true;
    try {
      const res = await api.getFlags(this.activeProductFilter);
      this._flags = res.flags;
    } catch (e: unknown) {
      this._toast(e instanceof Error ? e.message : "Failed to load flags");
    } finally {
      this._loading = false;
    }
  }

  private async _update(key: string, env: FlagEnv, patch: { enabled?: boolean; rolloutPercent?: number }) {
    if (!this.activeProductFilter) return;
    try {
      await api.updateFlag(key, { product: this.activeProductFilter, env, ...patch });
      await this._load();
    } catch (e: unknown) {
      this._toast(e instanceof Error ? e.message : "Failed to update flag");
    }
  }

  private async _delete(key: string) {
    if (!this.activeProductFilter) return;
    try {
      await api.deleteFlag(key, this.activeProductFilter);
      await this._load();
    } catch (e: unknown) {
      this._toast(e instanceof Error ? e.message : "Failed to delete flag");
    }
  }

  private _viewAuditLog() {
    // The Audit Log page's Feature Flags tab already scopes to the shared
    // activeProductFilter, so no need to pass product context here.
    this.dispatchEvent(new CustomEvent("navigate", { detail: "/audit?tab=flags", bubbles: true, composed: true }));
  }

  private _filteredFlags(): FeatureFlag[] {
    const query = this._search.trim().toLowerCase();
    if (!query) return this._flags;
    return this._flags.filter(
      (flag) => flag.key.toLowerCase().includes(query) || flag.description.toLowerCase().includes(query),
    );
  }

  private _renderCell(flag: FeatureFlag, env: FlagEnv) {
    const cell = flag.envs[env];
    if (!cell) return html`<span class="audit-meta">—</span>`;
    return html`
      <div class="cell">
        <bee-toggle
          size="small"
          aria-label="Toggle ${flag.key} in ${env}"
          .checked=${cell.enabled}
          @bee-change=${(e: CustomEvent) => this._update(flag.key, env, { enabled: (e.target as HTMLInputElement).checked })}
        ></bee-toggle>
        <bee-input
          class="rollout"
          size="small"
          type="number"
          label="Rollout percentage for ${flag.key} in ${env}"
          sr-only-label
          .value=${String(cell.rolloutPercent)}
          ?disabled=${!cell.enabled}
          @bee-change=${(e: CustomEvent) => {
            const pct = Math.max(0, Math.min(100, Number((e.target as HTMLInputElement).value) || 0));
            this._update(flag.key, env, { rolloutPercent: pct });
          }}
        ></bee-input>
      </div>
    `;
  }

  render() {
    if (!this.activeProductFilter) {
      return html`<div class="empty">Select a product to manage its flags.</div>`;
    }
    if (this._loading) {
      return html`<div class="empty"><bee-spinner size="medium" type="blue"></bee-spinner></div>`;
    }

    // Master switch off: offer to enable via PR rather than show the matrix.
    if (!this._flagsEnabled) {
      return html`
        <bee-card type="neutral">
          <div class="card-content">
            <div class="enable-panel">
              <div class="enable-msg">
                HAWAII Flagship is not enabled for <strong>${formatProductName(this.activeProductFilter)}</strong>.
              </div>
              ${this._enablePrUrl
                ? html`<div class="audit-meta">
                    Enablement PR opened — flags become available after it merges and KV syncs.
                    <a href=${this._enablePrUrl} target="_blank" rel="noopener">View PR</a>.
                  </div>`
                : html`
                    <bee-button
                      label=${this._enabling ? "Opening PR..." : "Enable Flagship"}
                      bee-type="primary"
                      ?loading=${this._enabling}
                      ?disabled=${this._enabling}
                      @click=${() => this._enable()}
                    ></bee-button>
                    <div class="audit-meta">Opens a PR to turn on Flagship for this product in all environments.</div>
                  `}
            </div>
          </div>
        </bee-card>
      `;
    }

    const filteredFlags = this._filteredFlags();

    return html`
      <div class="controls">
        <bee-button label="Refresh" bee-type="secondary-grey" size="small" icon="refresh" @click=${() => this._load()}></bee-button>
        <bee-button label="View Audit Log" bee-type="secondary-grey" size="small" @click=${() => this._viewAuditLog()}></bee-button>
        <div class="spacer"></div>
        <bee-button label="Create Flag" bee-type="primary" size="small" icon="plus" @click=${() => { this._showCreate = true; }}></bee-button>
      </div>

      <bee-card type="neutral">
        <div class="card-content">
          ${this._flags.length > 0
            ? html`
              <bee-input
                class="search-input"
                size="small"
                .value=${this._search}
                placeholder="Search flags..."
                @bee-input=${(e: CustomEvent) => { this._search = (e.target as HTMLInputElement).value; }}
              ></bee-input>
            ` : nothing}
          ${this._flags.length === 0
            ? html`<div class="empty">No flags for ${formatProductName(this.activeProductFilter)} yet. Create one to get started.</div>`
            : filteredFlags.length === 0
            ? html`<div class="empty">No flags match "${this._search}".</div>`
            : html`
              <table class="flags-table">
                <thead>
                  <tr>
                    <th class="name-col">Flag</th>
                    ${ENVS.map((e) => html`<th class="env-col">${e}</th>`)}
                    <th class="env-col"></th>
                  </tr>
                </thead>
                <tbody>
                  ${filteredFlags.map((flag) => html`
                    <tr>
                      <td class="name-col">
                        <div class="flag-key">${flag.key}</div>
                        ${flag.description ? html`<div class="flag-desc">${flag.description}</div>` : nothing}
                      </td>
                      ${ENVS.map((e) => html`<td class="env-col">${this._renderCell(flag, e)}</td>`)}
                      <td class="env-col">
                        <bee-button label="Delete" bee-type="destructive-tertiary" size="small" @click=${() => this._delete(flag.key)}></bee-button>
                      </td>
                    </tr>
                  `)}
                </tbody>
              </table>
            `}
        </div>
      </bee-card>

      <create-flag-dialog
        .open=${this._showCreate}
        .product=${this.activeProductFilter}
        @close=${() => { this._showCreate = false; }}
        @flag-created=${() => { this._showCreate = false; this._load(); }}
      ></create-flag-dialog>
    `;
  }
}
